/**
 * Words with several looks inside one board item (some bold, some underlined, others not), as
 * Miro writes them: runs of text, each with its own style keys ({@link RUN_KEYS}, the same words
 * as an item's whole style: `weight: "bold"`, `italic`/`underline`/`strike`: `"yes"`, `color`,
 * `highlight`, `size`, `font`).
 *
 * Kept in the item's `style.rich` as JSON (`[{"t":"Hello "},{"t":"world","underline":"yes"}]`)
 * beside its plain words in `node.name`, which everything else reads (search, the inspector, the
 * AI). The runs count only while their words are the node's name: words changed elsewhere drop
 * them. Pure: no DOM.
 */
import { FONTS, pixels, safeColour, TEXT_SIZES, type Css, type Style } from "./elements";

/** The style keys a run of words may hold. */
export const RUN_KEYS = ["weight", "italic", "underline", "strike", "color", "highlight", "size", "font"] as const;

/** One of {@link RUN_KEYS}. */
export type RunKey = (typeof RUN_KEYS)[number];

/** Words with one look. */
export interface Run {
  /** The words (may hold line breaks). */
  text: string;
  /** Their own look, by key (unset: the item's). */
  style: Partial<Record<RunKey, string>>;
}

/** Whether `key` is one of {@link RUN_KEYS}. */
export function isRunKey(key: string): key is RunKey {
  return (RUN_KEYS as readonly string[]).includes(key);
}

/** The words of `runs`. */
export function plainOf(runs: readonly Run[]): string {
  return runs.map((r) => r.text).join("");
}

/** Whether two runs' looks are the same. */
function sameStyle(a: Run["style"], b: Run["style"]): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<RunKey>;
  for (const k of keys) if ((a[k] ?? "") !== (b[k] ?? "")) return false;
  return true;
}

/** `runs` without empty ones, neighbours of the same look joined, unset keys dropped. */
export function tidyRuns(runs: readonly Run[]): Run[] {
  const out: Run[] = [];
  for (const r of runs) {
    if (!r.text) continue;
    const style: Run["style"] = {};
    for (const k of RUN_KEYS) if (r.style[k]) style[k] = r.style[k];
    const last = out[out.length - 1];
    if (last && sameStyle(last.style, style)) last.text += r.text;
    else out.push({ text: r.text, style });
  }
  return out;
}

/** The runs kept in `rich` (an item's `style.rich`) when their words are `name`; else `name` with no look of its own. */
export function readRich(rich: string | undefined, name: string): Run[] {
  const plain = name ? [{ text: name, style: {} }] : [];
  if (!rich) return plain;
  try {
    const data: unknown = JSON.parse(rich);
    if (!Array.isArray(data)) return plain;
    const runs: Run[] = data.flatMap((d): Run[] => {
      if (!d || typeof d !== "object" || typeof (d as { t?: unknown }).t !== "string") return [];
      const style: Run["style"] = {};
      for (const k of RUN_KEYS) {
        const v = (d as Record<string, unknown>)[k];
        if (typeof v === "string" && v) style[k] = v;
      }
      return [{ text: (d as { t: string }).t, style }];
    });
    return plainOf(runs) === name ? tidyRuns(runs) : plain;
  } catch {
    return plain;
  }
}

/** Whether some run has a look of its own. */
export function hasOwnLooks(runs: readonly Run[]): boolean {
  return runs.some((r) => Object.keys(r.style).length > 0);
}

/** `runs` as `style.rich` holds them; `""` when no run has a look of its own (the plain name says it all). */
export function writeRich(runs: readonly Run[]): string {
  const tidy = tidyRuns(runs);
  return hasOwnLooks(tidy) ? JSON.stringify(tidy.map((r) => ({ t: r.text, ...r.style }))) : "";
}

/** `runs` cut so that a run starts at `at` (a character offset). */
function splitAt(runs: readonly Run[], at: number): Run[] {
  const out: Run[] = [];
  let pos = 0;
  for (const r of runs) {
    const end = pos + r.text.length;
    if (at > pos && at < end) out.push({ text: r.text.slice(0, at - pos), style: r.style }, { text: r.text.slice(at - pos), style: r.style });
    else out.push(r);
    pos = end;
  }
  return out;
}

/** `runs` with `key` set to `value` (`""` unsets it) on the words from `start` to `end`. */
export function styleRange(runs: readonly Run[], start: number, end: number, key: RunKey, value: string): Run[] {
  const [a, b] = start <= end ? [start, end] : [end, start];
  let pos = 0;
  const out = splitAt(splitAt(runs, a), b).map((r) => {
    const from = pos;
    pos += r.text.length;
    if (from < a || from >= b) return r;
    const style = { ...r.style };
    if (value) style[key] = value;
    else delete style[key];
    return { text: r.text, style };
  });
  return tidyRuns(out);
}

/**
 * The value `key` has all along the words from `start` to `end`, a run's own else `whole`'s (the
 * item's); `null` when it changes along them.
 */
export function rangeValue(runs: readonly Run[], start: number, end: number, key: RunKey, whole: Style = {}): string | null {
  const [a, b] = start <= end ? [start, end] : [end, start];
  let pos = 0;
  let found: string | null = null;
  for (const r of runs) {
    const from = pos;
    pos += r.text.length;
    if (pos <= a || from >= b) continue;
    const v = r.style[key] ?? whole[key] ?? "";
    if (found === null) found = v;
    else if (found !== v) return null;
  }
  return found ?? (whole[key] ?? "");
}

/** `runs` with `key` taken out of every run (the whole item's look then says it). */
export function withoutKey(runs: readonly Run[], key: RunKey): Run[] {
  return tidyRuns(runs.map((r) => ({ text: r.text, style: Object.fromEntries(Object.entries(r.style).filter(([k]) => k !== key)) })));
}

/** `runs` where every run without its own `key` gets `value` (the item's, moved down into its words). */
export function pushDown(runs: readonly Run[], key: RunKey, value: string): Run[] {
  return tidyRuns(runs.map((r) => (r.style[key] ? r : { text: r.text, style: { ...r.style, [key]: value } })));
}

/** A run's own look as CSS declarations (values checked first). */
export function runCss(style: Run["style"]): Css {
  const css: Css = {};
  if (style.weight) css["font-weight"] = style.weight === "bold" ? "700" : "400";
  if (style.italic) css["font-style"] = style.italic === "yes" ? "italic" : "normal";
  const lines = [style.underline === "yes" ? "underline" : "", style.strike === "yes" ? "line-through" : ""].filter(Boolean).join(" ");
  if (lines) css["text-decoration-line"] = lines;
  const colour = safeColour(style.color);
  if (colour) css.color = colour;
  const highlight = safeColour(style.highlight);
  if (highlight) css["background-color"] = highlight;
  if (style.size) css["font-size"] = `${pixels(style.size, 16, TEXT_SIZES.min, TEXT_SIZES.max)}px`;
  if (style.font && FONTS[style.font]) css["font-family"] = FONTS[style.font]!;
  return css;
}
